<template lang="pug">
    div(v-if="currentUserIsManagerOrAdmin && showSimulator")
        v-btn(@click="simulatorDialog = true" outlined color="primary" :disabled="disabled" :small="isCustomerSimulation" :class="isCustomerSimulation ? 'mr-2' : ''")
                    v-icon(left) mdi-transit-connection-variant
                    | Enrollment Simulator

        v-dialog(v-model="simulatorDialog" max-width="none" width="auto")
            v-card.d-flex.flex-column(:style="{ height: formSubmitted ? '70vh' : 'auto' }")
                v-card-title 
                    | {{ formSubmitted ? `${enrollableType} Simulation Results` : 'Enrollment Simulator' }}
                v-card-text.flex-grow-1.d-flex.flex-column.align-center.pb-0(v-if="!formSubmitted" style="width: 800px;")
                    v-row.d-flex.flex-row.justify-space-between.align-center.width-100-percent(style="gap: 10px; flex-wrap: nowrap;")
                        v-col.d-flex.flex-column.justify-center.align-center.mt-6(cols="6")
                            v-date-picker.mb-0(
                                v-model="simulatorForm.date"
                                label="Date"
                                no-title
                            )
                            v-text-field(
                                v-model="simulatorForm.time"
                                label="Time"
                                type="time"
                                style="width: 70%; margin-top: -15px;"
                            )
                        v-col.d-flex.flex-column(cols="6" style="gap: 20px;")
                            template(v-if="isCustomerSimulation")
                                v-autocomplete(
                                    v-model="selectedCampaignId"
                                    label="Campaign Name"
                                    :items="campaigns"
                                    item-text="campaign_name"
                                    item-value="campaign_id"
                                    clearable
                                )
                                div.d-flex.flex-column.gap-2
                                    .text-uppercase.text-small Query Params
                                    .text-black {{ selectedCampaign ? selectedCampaign.total_criterions : '---' }}
                                div.d-flex.flex-column.gap-2
                                    .text-uppercase.text-small Customer
                                    .text-black {{ customer.customer.full_name }}
                                div.d-flex.flex-column.gap-2
                                    .text-uppercase.text-small Organization
                                    .text-black {{ organizationName }}

                            template(v-else)
                                div.d-flex.flex-column.gap-2
                                    .text-uppercase.text-small Campaign Name
                                    .text-black {{ campaign.campaign_name }}
                                div.d-flex.flex-column.gap-2
                                    .text-uppercase.text-small Query Params
                                    .text-black {{ campaign.total_criterions }}
                                div.d-flex.flex-column.gap-2(v-if="isProspectSimulation")
                                    .text-uppercase.text-small Prospect
                                    .text-black {{ customer.first_name }} {{ customer.last_name }}
                                div.d-flex.flex-column.gap-2
                                    .text-uppercase.text-small Organization
                                    .text-black {{ campaign.organization_name }}
                            div.d-flex.flex-column.gap-2
                                .text-uppercase.text-small Enrollment Type
                                .text-black {{ enrollableType }}

                v-card-text.flex-grow-1.overflow-y-auto(v-if="formSubmitted" style="width: 1000px;")
                    SimulationResults(
                        :campaign-name="campaign ? campaign.campaign_name : selectedCampaign.campaign_name"
                        :customer="customer"
                        :date="date"
                        :time="time"
                        :enrollable-type="enrollableType"
                        :enroller-run="enrollerRun"
                        :formSubmitted="formSubmitted"
                        :is-loading="isLoading"
                        :organization-name="campaign ? campaign.organization_name : organizationName"
                        :total-criterions="campaign ? campaign.total_criterions : selectedCampaign.total_criterions"
                    )


                v-card-actions.flex-shrink-0
                    v-spacer
                    v-btn.ma-2(color="var(--volie-blue)" outlined ripple @click='simulatorDialog = false; formSubmitted = false;')
                        v-icon mdi-window-close
                        | Close
                    v-btn(
                        color="primary" 
                        outlined 
                        ripple 
                        @click="formSubmitted ? createEnrollerRun(true) : createEnrollerRun(false)"
                        :disabled="noCampaign"
                    )
                        v-icon(left) mdi-transit-connection-variant
                        | {{ formSubmitted ? 'New Simulation' : 'Run Simulation' }}

</template>

<script lang="ts">
import Vue from 'vue';
import VolieState from '@/store';

// Components
import SimulationResults from '@/components/enroller_runs/simulator/simulation_results.vue';

// Data
import {
    GetCampaigns,
    CreateEnrollerRun
} from '@/data';

// Helpers
import { currentUserIsManagerOrAdmin } from '@/helpers/user';

export default Vue.extend({
    data() {
        return {
            bus: VolieState.websocketClient().eventBus(),
            campaigns: [],
            date: '',
            enrollerRunId: null,
            time: '',
            enrollerRun: null,
            formSubmitted: false,
            isLoading: false,
            organizationName: null,
            selectedCampaignId: null,
            simulatorDialog: false,
            simulatorForm: {
                date: null,
                time: null,
            },
        }
    },
    methods: {
        createEnrollerRun(isFormReset: boolean) {
            this.formSubmitted = this.$set(this, 'formSubmitted', !this.formSubmitted);

            if (isFormReset) {
                this.resetForm();
                return;
            }

            if (this.campaign?.campaign_id === 0 && this.selectedCampaignId === null) {
                return;
            }

            if ((this.isCustomerSimulation || this.isProspectSimulation) && !this.customer) {
                return;
            }

            if (this.currentUser.user_id === 0) {
                return;
            }

            //set date and time
            this.date = this.simulatorForm.date;
            this.time = this.simulatorForm.time;

            if (!this.simulatorForm.date) {
                this.date = this.getCurrentDate();
            } 
            if (!this.simulatorForm.time) {
                this.time = this.getCurrentTime();
            }

            const options = {
                campaign_id: this.campaign?.campaign_id || this.selectedCampaignId,
                customer_id: this.customerId,
                prospect_id: this.customer?.prospect_id || null,
                purpose: 'campaign_simulation',
                run_async: true,
                simulation_date: this.date,
                simulation_time: this.time,
                simulation_user_id: this.currentUser.user_id,
            }

            this.isLoading = true;

            CreateEnrollerRun(options).then((response) => {
                if (response?.Data?.enroller_run) {
                    this.enrollerRun = response?.Data?.enroller_run;
                    this.enrollerRunId = response?.Data?.enroller_run?.id;
                }
                this.isLoading = false;
            });

        },
        getCampaigns() {
            if (!this.isCustomerSimulation || !this.customer || !this.customer.organization_id) {
                return;
            }

            const options = {
                is_active: true,
                organization_id: this.customer.organization_id,
            }

            GetCampaigns(options).then((response) => {
                if (!!response?.Data && !!response?.Data?.campaigns && response?.Data?.campaigns?.length > 0) {
                    this.campaigns = response?.Data?.campaigns;
                    this.organizationName = response?.Data?.campaigns[0]?.organization_name;
                }
            });
        },
        getCurrentDate() {
            const date = new Date();
            const currentDate = date.toISOString().split('T')[0];
            return currentDate;
        },
        getCurrentTime() {
            const date = new Date();
            const currentTime = date.toISOString().split('T')[1].split('.')[0];
            const trimmedTime = currentTime.split(':').slice(0, 2).join(':');
            return trimmedTime;
        },
        onEnrollerRunUpdate(response) {
            if (response?.Data?.enroller_run_id === this.enrollerRunId) {
                this.enrollerRun = response?.Data;
            }
        },
        resetForm() {
            this.simulatorForm = {
                date: null,
                time: null,
            }
        }
    },
    computed: {
        currentUser() {
            return VolieState.currentUser();
        },
        currentUserIsManagerOrAdmin() {
            return currentUserIsManagerOrAdmin();
        },
        customerId() {
            if (!this.isCampaignSimulation) {
                return this.customer?.customer_id;
            }
            return null;
        },
        isCustomerSimulation() {
            return this.enrollableType === 'Customer';
        },
        isProspectSimulation() {
            return this.enrollableType === 'Prospect';
        },
        selectedCampaign() {
            if (!this.selectedCampaignId) return null;
            return this.campaigns.find(campaign => campaign.campaign_id === this.selectedCampaignId);
        },
        showSimulator() {
            if (this.isCustomerSimulation) {
                return this.source === 'admin';
            }
            return true;
        },
        noCampaign() {
            return !this.campaign && !this.selectedCampaignId;
        },
    },
    components: {
        SimulationResults,
    },
    props: {
        campaign: Object,
        customer: Object,
        disabled: Boolean,
        enrollableType: String,
        source: String,
    },
    created() {
        if (this.isCustomerSimulation) {
            this.getCampaigns();
        }

        this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "enrollerRunUpdate" }}, this.onEnrollerRunUpdate);
    },
    destroyed() {
        this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "enrollerRunUpdate" }}, this.onEnrollerRunUpdate);
    },
})
</script>

<style lang="scss" scoped>
.event-container {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--off-white);
    border-radius: 10px;
    padding: 10px;
    width: 100%;
    margin: 20px;
}

.event-summary:not(:last-of-type) {
    border-bottom: 1px solid var(--off-white);
}
</style>